import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  viewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { SelectItem } from '@openng/optimus-ui/api';
import { forkJoin, interval, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { MONLOG_DETAILS_CONTAINER } from 'src/app/constants/common.constants';
import {
  A_AND_E_LOG_CATEGORY,
  A_AND_E_MAINTENANCE,
  M_AND_R_LOG_CATEGORY,
  M_AND_R_MAINTENANCE,
} from 'src/app/constants/monlog-constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { MonLog } from 'src/app/model/monlogs/mon-log';
import { MonlogsApiService } from 'src/app/services/api/monlogs-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { MonlogsBuilderService } from 'src/app/services/builder/monlogs-builder.service';
import { getMonLogColumns } from '../definitions/all-mon-log-table-definition';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import { MonLogType } from 'src/app/model/admin/mon-log-type';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { AllLogsTableService } from 'src/app/services/table/all-logs-table.service';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { Panel } from '@openng/optimus-ui/panel';
import { MonLogMobileFilterComponent } from '../components/mon-log-mobile-filter.component';
import { ProgressSpinnerComponent } from '../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../core/components/general-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MonLogSortOrQuery } from 'src/app/model/monlogs/mon-log-search-result';

@Component({
  selector: 'app-monlogs-all-logs-search-container',
  templateUrl: './monlogs-all-logs-search-container.component.html',
  styleUrls: ['./monlogs-all-logs-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    BusinessUnitFilterComponent,
    Select,
    FormsModule,
    ButtonDirective,
    Panel,
    MonLogMobileFilterComponent,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class MonlogsAllLogsSearchContainerComponent
  extends SearchContainerComponent<MonLog>
  implements OnInit, OnDestroy, AfterViewInit
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _monLogsApi = inject(MonlogsApiService);
  protected _monLogBuilder = inject(MonlogsBuilderService);
  protected _authApi = inject(AuthApiService);
  protected _tableService = inject(AllLogsTableService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  allLogs: any[] = [];
  activeLogTypes: any[] = [];
  logsLoading: boolean = false;
  availableCompanies: any[] = [];
  logsLoadingEvent = new EventEmitter<any>();
  canCreate = false;
  canEdit = false;
  isOneCall = false;
  additionalFilters = [];
  actions: any[] = [];
  selectedAction: any = 'select';
  mobileFilterCollapsed = true;
  exportFilename: string = '';
  selectMessage: string = '';
  private _isRefreshing = false;
  refreshSubscription?: Subscription;
  availableTypes: MonLogType[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._tableService);
    this.initialize();

    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refresh(false);
    });
  }

  ngAfterViewInit(): void {
    this.applyPreviousFilters();
    this.applyPreviousData();
  }

  initialize() {
    this.columns = getMonLogColumns(this.isMobileDevice());

    const breadCrumb$ = this._translateService.get('MONLOG.SCREEN.ALL_LOGS');
    const header$ = this._translateService.get('MONLOG.SCREEN.ALL_LOGS');
    const exportFilename$ = this._translateService.get(
      'MONLOG.EXPORT_FILE_NAME.ALL_LOGS'
    );
    forkJoin([header$, exportFilename$, breadCrumb$])
      .pipe(take(1))
      .subscribe((mes) => {
        if (this._deviceService.isMobile()) {
          this._breadCrumbBuilderService.resetAndAddBreadCrumb({
            label: mes[2],
            routerLink: null,
          });
        } else {
          this._tabService.updateActiveTabLabel(mes[0]);
        }
        this.screenName = mes[0];
        this.exportFilename = mes[1];
      });

    this._monLogsApi.getMonLogTypes().subscribe((types) => {
      this.availableTypes = types;
    });

    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MON_LOGS.CREATE_MON_LOGS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MON_LOGS.EDIT_MON_LOGS,
    ]);
    this.isOneCall = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.MONITORING_CENTER.MON_LOGS.EMERGENCY_LOCATE_LOGS
        .CREATE_EMERGENCY_LOCATE_LOGS,
    ]);
    this.updateActionsList();
  }

  updateActionsList() {
    const selectAction$ = this._translateService.get(
      'MONLOG.LABEL.SELECT_ACTION'
    );
    const newCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_CALL'
    );
    const newMRCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_MR_CALL'
    );
    const newAECall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_AE_CALL'
    );
    const newControllerCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_CONTROLLER_CALL'
    );
    const newOneCall$ = this._translateService.get(
      'MONLOG.LABEL.BUTTONS.NEW_ONE_CALL'
    );

    let select,
      newControllerCall,
      newOneCall,
      newCall,
      newMRCall,
      newAECall = null;
    forkJoin([
      selectAction$,
      newControllerCall$,
      newOneCall$,
      newCall$,
      newMRCall$,
      newAECall$,
    ])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        newControllerCall = messages[1];
        newOneCall = messages[2];
        newCall = messages[3];
        newMRCall = messages[4];
        newAECall = messages[5];
        this.selectMessage = select;

        if (this.canCreate) {
          this.actions = [
            { label: select, value: 'select' },
            { label: newControllerCall, value: 'new-controller-call' },
            { label: newOneCall, value: 'new-one-call' },
            { label: newCall, value: 'new-call' },
            { label: newMRCall, value: 'new-mr-call' },
            { label: newAECall, value: 'new-ae-call' },
          ];
        } else if (this.isOneCall) {
          this.actions = [
            { label: select, value: 'select' },
            { label: newOneCall, value: 'new-one-call' },
          ];
        }

        // default actions here: Send to Day Crew, etc.
        this.actions = [...this.actions];
      });
  }

  actionEvent($event: SelectChangeEvent) {
    if ($event.value) {
      this.selectedAction = { label: this.selectMessage, value: 'select' };
      if ($event.value === 'new-call') {
        this.newCall('N');
      } else if ($event.value === 'new-mr-call') {
        this.newCall('MR');
      } else if ($event.value === 'new-ae-call') {
        this.newCall('AE');
      } else if ($event.value === 'new-controller-call') {
        this.newCall('I');
      } else if ($event.value === 'new-one-call') {
        this.newCall('O');
      }
    }
  }

  ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
  }

  newCall(category = 'N') {
    const monLog = this._monLogBuilder.buildNullMonLog(category);
    let typeArray: SelectItem[] = [];
    if (category === 'O') {
      typeArray = [
        ...this.availableTypes
          .filter((t) => t.name.toLowerCase() === 'emergency locate')
          .map((t) => ({ label: t.name, value: t.id })),
      ];
    } else if (category === M_AND_R_LOG_CATEGORY) {
      typeArray = [
        ...this.availableTypes
          .filter(
            (t) => t.name.toLowerCase() === M_AND_R_MAINTENANCE.toLowerCase()
          )
          .map((t) => ({ label: t.name, value: t.id })),
      ];
    } else if (category === A_AND_E_LOG_CATEGORY) {
      typeArray = [
        ...this.availableTypes
          .filter(
            (t) => t.name.toLowerCase() === A_AND_E_MAINTENANCE.toLowerCase()
          )
          .map((t) => ({ label: t.name, value: t.id })),
      ];
    }

    monLog.MonLogTypes = typeArray as any as MonLogType[];
    this._tableService.setSelected(monLog);
    const tab = this._tabService.buildNewTab(MONLOG_DETAILS_CONTAINER, true);
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab, true);
    }
  }

  /**
   * Method called when a row double click event is fired.  This navigates to the details page.
   * @param $event - Event from the double click event.
   */
  changeViewEvent($event: { data: MonLog }) {
    this._tableService.setSelected($event.data);

    const tab = this._tabService.buildNewTab(
      MONLOG_DETAILS_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits as any;

    const query = this._monLogBuilder.buildMonLogQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort: any = {};
    if ($event.lazy && $event.lazy.sortField) {
      // sorting
      if ($event.lazy.sortField === 'statusName') {
        sort['logStatus'] =
          $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'ASC' : 'DESC';
      } else {
        sort[$event.lazy.sortField as string] =
          $event.lazy.sortOrder && $event.lazy.sortOrder === 1 ? 'ASC' : 'DESC';
      }

      this._tableService.setLastSortField($event.lazy.sortField as string);
      this._tableService.setLastSortDirection($event.lazy.sortOrder as number);
    } else {
      sort['createdAt'] = 'DESC';
      this._tableService.setLastSortField('createdAt');
      this._tableService.setLastSortDirection(0);
    }

    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._monLogsApi
        .queryForMonLogs(
          req.pageSize,
          req.page,
          query as MonLogSortOrQuery,
          sort
        )
        .subscribe({
          next: (connection) => {
            this.totalRecords = connection.totalRecords;
            this.allLogs = [...connection.items];
            this.elements = [...this.allLogs];
            this.loading = false;
            this._isRefreshing = false;
          },
          error: (error) => {
            console.error(error);
            this._logAndMessage.translateToErrorAlert({
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
              bodyKey: 'MONLOG.MESSAGES.ERROR.LOAD_LOGS',
            });
            this.loading = false;
            this._isRefreshing = false;
          },
        });
    }
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! });
  }

  mobileFilter($event: any) {
    const newLazy: any = {
      filters: {
        ...this.grid()?.table()?.filters,
        logId: $event.logId
          ? {
              value: $event.logId,
              matchMode: 'contains',
            }
          : null,
        facilityName: $event.facilityName
          ? {
              value: $event.facilityName,
              matchMode: 'contains',
            }
          : null,
        logStatus: $event.statusName
          ? {
              value: $event.statusName,
              matchMode: 'contains',
            }
          : null,
      },
    };
    this.mobileFilterCollapsed = true;
    this._tableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! });
  }

  refresh(setLoading = true): void {
    this.lazyLoad({ lazy: this._tableService.getLastLazyLoad()! }, setLoading);
  }
}
